import { ChevronLeft, Eye, LogOut, ShieldCheck } from 'lucide-react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../../context/AuthContext';
import { ForgeTheme } from '../../hooks/useForgeTheme';
import {
  FORGE_CLEANING_TOOL_PATH,
  FORGE_DISTRACTORS_PATH,
  FORGE_MODEL_COMPARISON_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_RECOGNITION_REPLAY_PATH,
  FORGE_RECOGNITION_THRESHOLDS_CONFIG_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
} from '../../paths';
import { CleaningToolLink } from '../common/CleaningToolLink';
import { ForgeContextSwitcher } from './ForgeContextSwitcher';
import { FORGE_NAV_SECTIONS } from './forgeNavigation';
import { ThemeToggle } from './ThemeToggle';

interface ForgeSidebarProps {
  theme: ForgeTheme;
  width: number;
  isCollapsed: boolean;
  isResizing: boolean;
  isMobile?: boolean;
  toggleCollapse: () => void;
  hideSidebar: () => void;
  onDragStart: (e: React.MouseEvent) => void;
  toggleTheme: () => void;
  currentPath: string;
  hasRecognitionAdminAccess: boolean;
  hasRemoteDataCollectionAccess: boolean;
  recognitionOnly?: boolean;
  onMobileNavigate?: () => void;
}

const RECOGNITION_PATHS = new Set([
  FORGE_RECOGNITION_OKRS_PATH,
  FORGE_RECOGNITION_REPLAY_PATH,
  FORGE_MODEL_COMPARISON_PATH,
  FORGE_PR_REVIEW_PATH,
  FORGE_RECOGNITION_THRESHOLDS_CONFIG_PATH,
]);

const REMOTE_DC_LABELER_PATHS = new Set([
  FORGE_DISTRACTORS_PATH,
  FORGE_REMOTE_DC_TASK_LIST_PATH,
  FORGE_REMOTE_DC_TOOL_PATH,
]);

export const ForgeSidebar: React.FC<ForgeSidebarProps> = ({
  theme,
  width,
  isCollapsed,
  isResizing,
  isMobile = false,
  toggleCollapse,
  hideSidebar,
  onDragStart,
  toggleTheme,
  currentPath,
  hasRecognitionAdminAccess,
  hasRemoteDataCollectionAccess,
  recognitionOnly = false,
  onMobileNavigate,
}) => {
  const auth = useAuthContext();
  const { logout, canUseNonAdminPreview, enterNonAdminPreview } = auth;
  const navigate = useNavigate();
  const effectiveCollapsed = isCollapsed && !isMobile;
  const visibleSections = FORGE_NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.capability) return auth[item.capability];
      if (recognitionOnly) {
        return (
          (hasRecognitionAdminAccess && RECOGNITION_PATHS.has(item.path)) ||
          (hasRemoteDataCollectionAccess && REMOTE_DC_LABELER_PATHS.has(item.path))
        );
      }
      return (
        (hasRecognitionAdminAccess || !RECOGNITION_PATHS.has(item.path)) &&
        (hasRemoteDataCollectionAccess || !REMOTE_DC_LABELER_PATHS.has(item.path))
      );
    }),
  })).filter((section) => section.items.length > 0);

  const isActive = (path: string) => {
    if (path === FORGE_CLEANING_TOOL_PATH) return currentPath === path;
    return currentPath.startsWith(path);
  };

  const sidebarClasses = [
    'forge-sidebar',
    effectiveCollapsed ? 'is-collapsed' : '',
    isResizing ? 'is-resizing' : '',
    isMobile ? 'is-mobile' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <aside
      className={sidebarClasses}
      style={{ width: !isMobile && !effectiveCollapsed ? width : undefined }}
      aria-label="Forge navigation"
    >
      {/* Nav */}
      <nav className="sidebar-nav">
        {visibleSections.map((section) => (
          <div key={section.title} className="sidebar-section">
            <div className="sidebar-section-label">{section.title}</div>
            {section.items.map((item) => {
              const SidebarLink = item.path === FORGE_CLEANING_TOOL_PATH ? CleaningToolLink : Link;
              return (
                <SidebarLink
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive(item.path) ? 'active' : ''}`}
                  title={effectiveCollapsed ? item.label : undefined}
                  onClick={onMobileNavigate}
                >
                  {item.icon}
                  <span className="sidebar-item-label">{item.label}</span>
                </SidebarLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Collapse toggle */}
      <div className="sidebar-footer">
        <div className="sidebar-footer-utilities">
          {canUseNonAdminPreview ? (
            <button
              className="sidebar-footer-link"
              title={effectiveCollapsed ? 'Labeler preview' : undefined}
              onClick={() => {
                onMobileNavigate?.();
                enterNonAdminPreview();
              }}
            >
              <Eye />
              <span className="sidebar-collapse-label">Labeler preview</span>
            </button>
          ) : (
            <button
              className="sidebar-footer-link"
              title={effectiveCollapsed ? 'Admin' : undefined}
            >
              <ShieldCheck />
              <span className="sidebar-collapse-label">Admin</span>
            </button>
          )}

          <ForgeContextSwitcher variant={isMobile ? 'drawer' : 'sidebar'} />

          <ThemeToggle
            theme={theme}
            onToggle={toggleTheme}
            variant="sidebar"
            collapsed={effectiveCollapsed}
          />

          <button
            className="sidebar-footer-link"
            onClick={() => {
              onMobileNavigate?.();
              logout();
              navigate('/stores');
            }}
            title={effectiveCollapsed ? 'Sign out' : undefined}
          >
            <LogOut />
            <span className="sidebar-collapse-label">Sign out</span>
          </button>
        </div>

        {!isMobile ? (
          <>
            <div className="sidebar-footer-divider" />

            <button className="sidebar-collapse-btn" onClick={hideSidebar} title="Hide sidebar">
              <ChevronLeft />
              <span className="sidebar-collapse-label">Hide</span>
            </button>
          </>
        ) : null}
      </div>

      {!isMobile ? <div className="sidebar-drag-handle" onMouseDown={onDragStart} /> : null}
    </aside>
  );
};
